import React, { useEffect, useRef, useState } from 'react';
import { TooltipConstants } from '@Components/Tooltip';
import PropTypes from 'prop-types';
import PublishedParameters, { extractDefaultPublishedParameters } from '@Pages/RetroSynthesis/ProjectParametersDialog/PublishedParameters';
import ProjectParametersSection from '@Pages/RetroSynthesis/ProjectParametersDialog/ProjectParametersSection';
import PredictedParametersPai, {
  extractDefaultPredictedParametersPai,
  USED_BUILDING_BLOCKS,
  extractSelectedBuildingBlocks,
  getBlockArrayValues,
} from '@Pages/RetroSynthesis/ProjectParametersDialog/PredictedParametersPai';
import { InfoTooltipOnClick } from '@Components/InfoTooltip';
import isEmpty from 'lodash/isEmpty';
import Auth from '@Services/Auth';
import {
  isPredictiveRetrosynthesisEnabled,
  isIktosPredictiveRetrosynthesisEnabled,
  isPaiPredictiveRetrosynthesisEnabled,
} from '@Pages/RetroSynthesis/predictiveUtils';
import useHideTooltipsOnScroll from '@Components/Tooltip/useHideTooltipsOnScroll';
import { SECTION_TYPE } from '@Pages/RetroSynthesis/ProjectParametersDialog/anaytics';
import PanelBlock from './PanelBlock';
import { PREDICTED_PARAMS_SECTION_LABEL, PUBLISHED_PARAMS_SECTION_LABEL } from '../../ProjectParametersDialog/constants';
import PredictedParametersIktos, { extractDefaultPredictedParametersIktos } from '../../ProjectParametersDialog/PredictedParametersIktos';

import './OptionsPanel.scss';

/* eslint-disable react-hooks/exhaustive-deps */
function OptionsPanel({
  optionResponse,
  vendorsInfo,
  onProjectParamsChanged,
  initialPublishedParameters,
  initialPredictedParameters,
  handleValidationError,
}) {
  const paramsContainerRef = useRef(null);
  const isPredictedIktos = isIktosPredictiveRetrosynthesisEnabled();
  const isPredictedPai = isPaiPredictiveRetrosynthesisEnabled() && !isPredictedIktos;
  const showPredictedParams = isPredictedPai || isPredictedIktos;
  const isAnonymousUser = Auth.isAnonymousUser();
  const isPredictiveRSEnabled = isPredictiveRetrosynthesisEnabled();
  const [publishedParams, setPublishedParams] = useState(initialPublishedParameters || {});
  const [predictedParams, setPredictedParams] = useState(initialPredictedParameters || {});
  const [publishedEnabled, setPublishedEnabled] = useState(true);
  const [publishedEditMode, setPublishedEditMode] = useState(false);
  const [predictedEnabled, setPredictedEnabled] = useState(isPredictiveRSEnabled);
  const [predictedEditMode, setPredictedEditMode] = useState(true);
  const publishedParametersEditable = !showPredictedParams || publishedEditMode;
  const predictedParametersEditable = showPredictedParams && predictedEditMode;
  const tooltipId = TooltipConstants.ids.RetrosynthesisEditor;
  useEffect(() => {
    if (!optionResponse) {
      return;
    }

    // init default params if not passed
    if (isEmpty(initialPublishedParameters)) {
      setPublishedParams({ ...extractDefaultPublishedParameters(optionResponse) });
    }
    if (isEmpty(initialPredictedParameters)) {
      let defaultPredictedParams;
      if (isPredictedPai) {
        defaultPredictedParams = { ...extractDefaultPredictedParametersPai(optionResponse) };
      } else if (isPredictedIktos) {
        defaultPredictedParams = { ...extractDefaultPredictedParametersIktos(optionResponse) };
      }
      setPredictedParams(defaultPredictedParams);
    } else if (isPredictedPai) {
      // sync initial building blocks with the option response
      const buildingBlockElements = getBlockArrayValues(USED_BUILDING_BLOCKS, optionResponse);
      let matchedBBlibs = initialPredictedParameters.usedBuildingBlocks.filter((bb) =>
        buildingBlockElements.find((element) => element.name === bb)
      );
      if (matchedBBlibs.length === 0) {
        // select default if no matches
        matchedBBlibs = extractSelectedBuildingBlocks(USED_BUILDING_BLOCKS, optionResponse);
      }
      setPredictedParams({ ...initialPredictedParameters, usedBuildingBlocks: matchedBBlibs });
    }
  }, [optionResponse]);

  useEffect(() => {
    onProjectParamsChanged({
      ...(publishedEnabled && { publishedParams }),
      ...(predictedEnabled && { predictedParams }),
    });
  }, [publishedParams, predictedParams, publishedEnabled, predictedEnabled]);

  useHideTooltipsOnScroll(paramsContainerRef);

  return (
    <section className="retro-synthesis-query-options-panel">
      <PanelBlock className="retro-synthesis-query-options-panel--title-wrapper">
        <svg className="retro-synthesis-query-options-panel--title-icon">
          <use xlinkHref="#icon-cog" />
        </svg>
        Parameters
      </PanelBlock>
      <PanelBlock className="retro-synthesis-query-options-panel--parameters" ref={paramsContainerRef}>
        {showPredictedParams && (
          <ProjectParametersSection
            className="predicted"
            label="Predicted"
            infoTooltipAriaLabel={PREDICTED_PARAMS_SECTION_LABEL}
            infoTooltipIconTitle={PREDICTED_PARAMS_SECTION_LABEL}
            tooltip={
              isAnonymousUser ? (
                'Note: to use this feature please sign in first'
              ) : (
                <ul className="info-tooltip-content-list">
                  <li className="info-tooltip-content-list-item">
                    {/* eslint-disable-next-line react/no-unescaped-entities */}
                    Here you can alter the predictive retrosynthesis parameters to tailor your results. Please click "Edit" to modify them.{' '}
                  </li>
                  <li className="info-tooltip-content-list-item">(refer to Reaxys help system for more)</li>
                </ul>
              )
            }
            tooltipFor={tooltipId}
            sectionType={isPredictedPai ? SECTION_TYPE.PAI : SECTION_TYPE.IKTOS}
            editable={isPredictiveRSEnabled}
            selected={predictedEnabled}
            onSelectedChanged={(value) => {
              if (!value && !publishedEnabled) {
                setPublishedEnabled(true);
              }
              setPredictedEnabled(value);
            }}
            onEditSateChanged={setPredictedEditMode}
            editSate={predictedEditMode}
            disabled={isAnonymousUser}
            vendorsInfo={vendorsInfo}>
            {isPredictedPai && (
              <PredictedParametersPai
                onParamsChanged={(options) => setPredictedParams({ ...predictedParams, ...options })}
                projectParams={predictedParams}
                editable={predictedParametersEditable}
                tooltipFor={tooltipId}
                optionResponse={optionResponse}
              />
            )}
            {isPredictedIktos && (
              <PredictedParametersIktos
                onParamsChanged={(options) => setPredictedParams({ ...predictedParams, ...options })}
                projectParams={predictedParams}
                editable={predictedParametersEditable}
                tooltipFor={tooltipId}
                optionResponse={optionResponse}
                handleValidationError={handleValidationError}
              />
            )}
          </ProjectParametersSection>
        )}
        <ProjectParametersSection
          className="published"
          label="Published"
          infoTooltipAriaLabel={PUBLISHED_PARAMS_SECTION_LABEL}
          infoTooltipIconTitle={PUBLISHED_PARAMS_SECTION_LABEL}
          tooltip={
            <ul className="info-tooltip-content-list">
              <li className="info-tooltip-content-list-item">
                {/* eslint-disable-next-line react/no-unescaped-entities */}
                Here you can alter the published retrosynthesis parameters to tailor your results. Please click "Edit" to modify them.
              </li>
              <li className="info-tooltip-content-list-item">(refer to Reaxys help system for more)</li>
            </ul>
          }
          tooltipFor={tooltipId}
          editable={showPredictedParams}
          sectionType={SECTION_TYPE.PUBLISHED}
          selected={publishedEnabled}
          onSelectedChanged={(value) => {
            if (!value && isAnonymousUser) {
              return;
            }
            if (!value && !predictedEnabled) {
              setPredictedEnabled(true);
            }
            setPublishedEnabled(value);
          }}
          onEditSateChanged={setPublishedEditMode}
          editSate={publishedEditMode}>
          <PublishedParameters
            onParamsChanged={(options) => setPublishedParams({ ...publishedParams, ...options })}
            projectParams={publishedParams}
            editable={publishedParametersEditable}
            optionResponse={optionResponse}
            tooltipFor={tooltipId}
          />
        </ProjectParametersSection>
      </PanelBlock>
      <InfoTooltipOnClick place="left" multiline id={tooltipId} offset={{ left: -20, top: 10 }} scrollHide />
    </section>
  );
}

OptionsPanel.defaultProps = {
  onProjectParamsChanged: () => null,
  handleValidationError: () => null,
};

OptionsPanel.propTypes = {
  optionResponse: PropTypes.array,
  vendorsInfo: PropTypes.array,
  onProjectParamsChanged: PropTypes.func,
  initialPublishedParameters: PropTypes.object,
  initialPredictedParameters: PropTypes.object,
  handleValidationError: PropTypes.func,
};

export default OptionsPanel;
